import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { companiesAPI } from '../../services/api';
import { Building2, Plus, Trash2, Loader2, Users, MapPin, ChevronDown, ChevronRight, UserCog, User } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState({});

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data);
      // Auto-expand all companies
      const expanded = {};
      response.data.forEach(c => { expanded[c.id] = true; });
      setExpandedCompanies(expanded);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await companiesAPI.create(formData);
      toast.success('Empresa creada exitosamente');
      setDialogOpen(false);
      setFormData({ name: '', description: '' });
      loadCompanies();
    } catch (error) {
      toast.error('Error al crear empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta empresa? Se eliminarán todos los datos asociados.')) return;
    try {
      await companiesAPI.delete(id);
      toast.success('Empresa eliminada');
      loadCompanies();
    } catch (error) {
      toast.error('Error al eliminar empresa');
    }
  };

  const toggleExpand = (id) => {
    setExpandedCompanies(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'colaborador': return 'bg-green-50 text-green-700 border-green-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getRoleIcon = (role) => {
    return role === 'admin' ? UserCog : User;
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Gestión de Empresas
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Administra las empresas y sus usuarios asignados
            </p>
          </div>
          <Button 
            onClick={() => setDialogOpen(true)} 
            className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
            data-testid="add-company-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Empresa
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : companies.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay empresas registradas</p>
              <Button 
                onClick={() => setDialogOpen(true)} 
                className="mt-4 bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
              >
                Crear primera empresa
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {companies.map((company) => (
              <Card key={company.id} className="border border-slate-200" data-testid={`company-card-${company.id}`}>
                <Collapsible open={expandedCompanies[company.id]} onOpenChange={() => toggleExpand(company.id)}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CollapsibleTrigger className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer">
                        {expandedCompanies[company.id] ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                        <div className="w-12 h-12 bg-[#002FA7] rounded-sm flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-left">
                          <CardTitle className="text-xl">{company.name}</CardTitle>
                          {company.description && (
                            <p className="text-sm text-slate-500 mt-0.5">{company.description}</p>
                          )}
                        </div>
                      </CollapsibleTrigger>
                      
                      <div className="flex items-center gap-4">
                        {/* Stats badges */}
                        <div className="hidden sm:flex items-center gap-3">
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-sm">
                            <Users className="w-4 h-4 text-slate-600" />
                            <span className="text-sm font-medium">{company.stats?.total_users || 0}</span>
                            <span className="text-xs text-slate-500">usuarios</span>
                          </div>
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-sm">
                            <MapPin className="w-4 h-4 text-slate-600" />
                            <span className="text-sm font-medium">{company.stats?.sedes || 0}</span>
                            <span className="text-xs text-slate-500">sedes</span>
                          </div>
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleDelete(company.id); }}
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`delete-company-${company.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Mobile stats */}
                    <div className="flex sm:hidden items-center gap-3 mt-3 ml-8">
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded-sm">
                        <Users className="w-3 h-3 text-slate-600" />
                        <span className="text-xs font-medium">{company.stats?.total_users || 0} usuarios</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded-sm">
                        <MapPin className="w-3 h-3 text-slate-600" />
                        <span className="text-xs font-medium">{company.stats?.sedes || 0} sedes</span>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      {/* Stats summary */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 p-3 bg-slate-50 rounded-sm border border-slate-200">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#002FA7]">{company.stats?.total_users || 0}</p>
                          <p className="text-xs text-slate-500">Total Usuarios</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{company.stats?.admins || 0}</p>
                          <p className="text-xs text-slate-500">Administradores</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{company.stats?.colaboradores || 0}</p>
                          <p className="text-xs text-slate-500">Colaboradores</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-slate-600">{company.stats?.sedes || 0}</p>
                          <p className="text-xs text-slate-500">Sedes</p>
                        </div>
                      </div>
                      
                      {/* Users list */}
                      {company.users && company.users.length > 0 ? (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Usuarios Asignados
                          </h4>
                          <div className="border border-slate-200 rounded-sm overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Usuario</th>
                                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Email</th>
                                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Rol</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {company.users.map((user) => {
                                  const RoleIcon = getRoleIcon(user.role);
                                  return (
                                    <tr key={user.id} className="hover:bg-slate-50">
                                      <td className="py-2 px-3">
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-xs font-medium">
                                            {user.name?.charAt(0).toUpperCase()}
                                          </div>
                                          <span className="font-medium text-sm">{user.name}</span>
                                        </div>
                                      </td>
                                      <td className="py-2 px-3 text-sm text-slate-500">{user.email}</td>
                                      <td className="py-2 px-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium border ${getRoleBadge(user.role)}`}>
                                          <RoleIcon className="w-3 h-3" />
                                          {user.role === 'admin' ? 'Administrador' : 'Colaborador'}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 text-slate-400">
                          <Users className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">No hay usuarios asignados a esta empresa</p>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nueva Empresa</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la empresa</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="Ej: Empresa S.A."
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción (opcional)</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="rounded-sm"
                  placeholder="Descripción breve de la empresa"
                  data-testid="company-description-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-company-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default CompaniesPage;
